import { useSuspenseQuery } from "@apollo/client";
import {
  amber,
  blue,
  blueGrey,
  green,
  grey,
  indigo,
  purple,
} from "@mui/material/colors";
import { GridProps, Typography } from "@mui/material";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import DonutChart from "components/Dashboard/DonutChart";
import { gql } from "generated-graphql";
import { RegulatoryStatusOption, SubmissionStatus } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { useCallback, useMemo } from "react";
import { useCurrentUser } from "hooks/useCurrentUser";

const WASTE_SUBMISSIONS_SUMMARY_QUERY = gql(`
  query WasteSubmissionsSummary($tenantId: ID!, $filter: WasteSubmissionsSummaryFilter) {
    wasteSubmissionsSummary(tenantId: $tenantId, filter: $filter) {
      byStatus {
        status
        count
      }
      total
    }
  }
`);

// Status display configuration with colors matching waste overview palette
const STATUS_CONFIG: Record<SubmissionStatus, { label: string; color: string }> =
  {
    [SubmissionStatus.NotStarted]: {
      label: "Upcoming",
      color: amber[500], // Upcoming/pending work
    },
    [SubmissionStatus.InProgress]: {
      label: "In Progress",
      color: blue[400], // Active work
    },
    [SubmissionStatus.EncampInitialReview]: {
      label: "In Encamp Review",
      color: indigo[400], // Staff review (matches generator status palette)
    },
    [SubmissionStatus.InReview]: {
      label: "In Review",
      color: purple[300], // Customer review
    },
    [SubmissionStatus.ProcessingSubmission]: {
      label: "Awaiting Submission",
      color: blueGrey[400], // Waiting state
    },
    [SubmissionStatus.FilingComplete]: {
      label: "Submitted",
      color: green[500], // Complete/success
    },
    [SubmissionStatus.NotReporting]: {
      label: "Not Reporting",
      color: grey[400], // N/A state
    },
  };

type SubmissionStatusTileProps = GridProps & {
  filter?: {
    dueDateStart?: string;
    dueDateEnd?: string;
    states?: string[];
    statuses?: SubmissionStatus[];
    generatorStatuses?: RegulatoryStatusOption[];
    facilityTagIds?: string[];
  };
  onStatusClick?: (status: SubmissionStatus) => void;
  selectedStatus?: SubmissionStatus | null;
};

export function SubmissionStatusTile({
  filter,
  onStatusClick,
  selectedStatus,
  ...gridProps
}: SubmissionStatusTileProps) {
  return (
    <DashboardTile {...gridProps}>
      <SubmissionStatusContent
        filter={filter}
        onStatusClick={onStatusClick}
        selectedStatus={selectedStatus}
      />
    </DashboardTile>
  );
}

function SubmissionStatusContent({
  filter,
  onStatusClick,
  selectedStatus,
}: Pick<
  SubmissionStatusTileProps,
  "filter" | "onStatusClick" | "selectedStatus"
>) {
  const { tenantId } = useTenant();
  const { isStaff } = useCurrentUser();

  const { data } = useSuspenseQuery(WASTE_SUBMISSIONS_SUMMARY_QUERY, {
    variables: {
      tenantId: tenantId ?? "",
      filter: filter
        ? {
            dueDateStart: filter.dueDateStart,
            dueDateEnd: filter.dueDateEnd,
            states: filter.states,
            generatorStatuses: filter.generatorStatuses,
            facilityTagIds: filter.facilityTagIds,
          }
        : undefined,
    },
    fetchPolicy: "cache-and-network",
  });

  const summary = data?.wasteSubmissionsSummary;

  // Filter out staff-only statuses for non-staff users
  const visibleStatuses = useMemo(() => {
    const allStatuses = Object.values(SubmissionStatus);
    if (isStaff) {
      return allStatuses;
    }
    // Hide ENCAMP_INITIAL_REVIEW for non-staff users
    return allStatuses.filter(
      (status) => status !== SubmissionStatus.EncampInitialReview
    );
  }, [isStaff]);

  const chartData = useMemo(() => {
    if (!summary?.byStatus) return [];

    return visibleStatuses
      .map((status) => {
        const statusCount = summary.byStatus.find((s) => s.status === status);
        const config = STATUS_CONFIG[status];
        return {
          id: status,
          value: statusCount?.count ?? 0,
          label: `${config.label} (${statusCount?.count ?? 0})`,
          color: config.color,
        };
      })
      .filter((item) => item.value > 0); // Only show statuses with counts
  }, [summary, visibleStatuses]);

  const totalCount = summary?.total ?? 0;

  const selectedIndex = useMemo(() => {
    if (!selectedStatus) return null;
    return chartData.findIndex((item) => item.id === selectedStatus);
  }, [selectedStatus, chartData]);

  const handleItemClick = useCallback(
    (
      _event: React.MouseEvent<SVGPathElement, MouseEvent>,
      itemIdentifier: { dataIndex: number }
    ) => {
      const clickedItem = chartData[itemIdentifier.dataIndex];
      if (clickedItem && onStatusClick) {
        onStatusClick(clickedItem.id as SubmissionStatus);
      }
    },
    [chartData, onStatusClick]
  );

  const handleLegendItemClick = useCallback(
    (item: { id?: string | number }) => {
      if (item.id && onStatusClick) {
        onStatusClick(item.id as SubmissionStatus);
      }
    },
    [onStatusClick]
  );

  return (
    <>
      <DashboardTile.Header
        title="Submission Status"
        infoTooltip={<SubmissionStatusTooltip />}
      />
      <DashboardTile.ContentArea>
        {chartData.length > 0 ? (
          <DonutChart
            seriesData={chartData}
            containerHeight="250px"
            label={totalCount.toString()}
            subLabel="Total"
            selectedIndex={selectedIndex}
            onItemClick={handleItemClick}
            onLegendItemClick={handleLegendItemClick}
            legend={{ position: "right", direction: "column" }}
            colors={chartData.map((item) => item.color)}
          />
        ) : (
          <Typography color="text.secondary" textAlign="center" py={4}>
            No submissions found
          </Typography>
        )}
      </DashboardTile.ContentArea>
    </>
  );
}

function SubmissionStatusTooltip() {
  return (
    <Typography variant="caption">
      <Typography component="span" variant="caption" fontWeight={800}>
        Submission Status
      </Typography>{" "}
      shows the distribution of your submissions by their current status. Click
      on a segment to filter the list below.
    </Typography>
  );
}
